import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, ChevronLeft, ChevronRight, ArrowUpDown,
  ArrowUp, ArrowDown, Eye, Loader2, Mail,
  Calendar, MapPin, Target, TrendingUp
} from 'lucide-react';

const SORT_OPTIONS = [
  { key: 'last_visit', label: 'Last Visit' },
  { key: 'total_visits', label: 'Total Visits' },
  { key: 'lead_score', label: 'Lead Score' },
];

const PAGE_SIZE = 20;

const WebTrackingVisitors = ({ visitors, loading, onFetchVisitors, onSelectVisitor }) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('last_visit');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    onFetchVisitors({ page, pageSize: PAGE_SIZE, sortBy, sortOrder });
  }, [page, sortBy, sortOrder, onFetchVisitors]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />;
    return sortOrder === 'desc'
      ? <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
      : <ArrowUp className="w-3.5 h-3.5 text-primary-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  };

  if (loading && !visitors) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-500">Loading visitors...</span>
      </div>
    );
  }

  const visitorList = visitors?.visitors || [];
  const total = visitors?.total || 0;
  const totalPages = visitors?.total_pages || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Identified Visitors
          <span className="text-sm font-normal text-gray-500 ml-2">({total})</span>
        </h3>
      </div>

      {visitorList.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No identified visitors yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Visitors will appear here after they click a link from your tracked emails
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Visitor
                    </th>
                    {SORT_OPTIONS.map(opt => (
                      <th
                        key={opt.key}
                        className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                        onClick={() => handleSort(opt.key)}
                      >
                        <div className="flex items-center gap-1">
                          {opt.label}
                          <SortIcon field={opt.key} />
                        </div>
                      </th>
                    ))}
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visitorList.map((visitor, idx) => (
                    <motion.tr
                      key={visitor.lead_email}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.02 }}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onSelectVisitor(visitor.lead_email)}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {visitor.first_name || visitor.last_name
                              ? `${visitor.first_name || ''} ${visitor.last_name || ''}`.trim()
                              : visitor.lead_email?.split('@')[0]}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{visitor.lead_email}</span>
                          </div>
                          {visitor.company && (
                            <span className="text-xs text-gray-400">{visitor.company}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-700">{formatDate(visitor.last_visit)}</span>
                        </div>
                        <span className="text-xs text-gray-400">First: {formatDate(visitor.first_visit)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{visitor.total_visits}</span>
                          <span className="text-xs text-gray-400">
                            ({visitor.sessions} session{visitor.sessions !== 1 ? 's' : ''})
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">{visitor.unique_pages} pages</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-sm font-semibold text-green-700">{visitor.lead_score ?? '—'}</span>
                        </div>
                        <span className="text-xs text-gray-400">+{visitor.total_score_from_visits || 0} from visits</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {visitor.most_common_country && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">{visitor.most_common_country}</span>
                            </div>
                          )}
                          {visitor.top_campaign && (
                            <div className="flex items-center gap-1">
                              <Target className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                                {visitor.top_campaign}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelectVisitor(visitor.lead_email); }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="View visit history"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} ({total} visitors)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading && visitors && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-500 text-sm">Updating...</span>
        </div>
      )}
    </div>
  );
};

export default WebTrackingVisitors;
